import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { orpc } from "@/lib/orpc";
import { switchSession } from "@/lib/session/session";
import { useCan } from "@/lib/policy";
import { AddonsCard } from "./-components/addons-card";
import { FeatureOverrides } from "./-components/feature-overrides";
import { PlatformEnterDialog } from "@/routes/_authenticated/-components/platform-enter-dialog";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/plans-modules/$organizationId",
)({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.administration.organizations.get.queryOptions({
        input: { organizationId: params.organizationId },
      }),
    ),
  component: OrganizationDetailPage,
});

function OrganizationDetailPage() {
  const { organizationId } = Route.useParams();
  const { data: org } = useSuspenseQuery(
    orpc.administration.organizations.get.queryOptions({ input: { organizationId } }),
  );
  const plans = useQuery(orpc.administration.organizations.plans.queryOptions());
  const canManage = useCan("platform:manage");
  const queryClient = useQueryClient();
  const [suspending, setSuspending] = useState(false);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.organizations.key() });
  const update = useMutation(
    orpc.administration.organizations.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Organization updated");
        await invalidate();
      },
      onError: (e) => toast.error(errorWords(e)),
    }),
  );
  const [entering, setEntering] = useState(false);
  const enter = useMutation(
    orpc.administration.organizations.enter.mutationOptions({
      onSuccess: () => {
        setEntering(false);
        switchSession(queryClient, "/");
      },
      onError: (e) => toast.error(errorWords(e)),
    }),
  );

  return (
    <>
      <PlatformEnterDialog
        organizationName={entering ? org.name : null}
        pending={enter.isPending}
        onSubmit={(reason) => enter.mutate({ organizationId, reason })}
        onClose={() => setEntering(false)}
      />
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {org.name}
            <Badge variant={org.status === "active" ? "ok" : "destructive"} className="capitalize">
              {org.status}
            </Badge>
          </span>
        }
        meta={
          <>
            <Link to="/settings/plans-modules" className="hover:underline">
              All organizations
            </Link>
            {", "}
            {org.slug}, {org.memberCount} members, {org.facilities.length} facilities
          </>
        }
        actions={
          canManage ? <Button onClick={() => setEntering(true)}>Enter organization</Button> : null
        }
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Plan & settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm">Plan</span>
              <Select
                value={org.planId ?? "none"}
                disabled={!canManage}
                onValueChange={(planId) =>
                  update.mutate({ organizationId, planId: planId === "none" ? null : planId })
                }
              >
                <SelectTrigger className="w-48" aria-label="Plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No plan (unrestricted)</SelectItem>
                  {(plans.data ?? []).map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm">Accepts Medicaid</span>
              <Switch
                checked={org.acceptsMedicaid}
                disabled={!canManage}
                aria-label="Accepts Medicaid"
                onCheckedChange={(acceptsMedicaid) =>
                  update.mutate({ organizationId, acceptsMedicaid })
                }
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm">Suspended</span>
              <Switch
                checked={org.status === "suspended"}
                disabled={!canManage}
                aria-label="Suspended"
                onCheckedChange={(suspended) =>
                  suspended
                    ? setSuspending(true)
                    : update.mutate({ organizationId, status: "active" })
                }
              />
            </div>
            <div>
              <p className="text-faint mb-1 text-xs font-medium">Facilities</p>
              <ul className="space-y-1 text-sm">
                {org.facilities.map((facility) => (
                  <li key={facility.id} className="flex justify-between">
                    <span>{facility.name}</span>
                    <span className="text-muted-foreground font-mono text-xs">{facility.code}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
        <AddonsCard organizationId={organizationId} canManage={canManage} />
        <Card>
          <CardHeader>
            <CardTitle>Features</CardTitle>
          </CardHeader>
          <CardContent>
            {canManage ? (
              <FeatureOverrides
                organizationId={organizationId}
                overrides={org.overrides}
                planFeatures={org.planFeatures}
              />
            ) : (
              <p className="text-muted-foreground text-sm">Read only.</p>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Suspension refuses every request the organization's people make, so
          it is asked about first; reactivating is the switch alone. */}
      {suspending ? (
        <RemoveConfirm
          title={`Suspend ${org.name}?`}
          description="Everyone in this organization is locked out of People AI until you reactivate it."
          confirmLabel="Suspend organization"
          onConfirm={() => update.mutate({ organizationId, status: "suspended" })}
          onClose={() => setSuspending(false)}
        />
      ) : null}
    </>
  );
}
